import axios from "axios";
import { t } from "i18next";
import cookie from "js-cookie";
import jwt_decode from "jwt-decode";
import posthog from "posthog-js";
import axiosInstance from "../helpers/axios";
import { ACCESS_TOKEN_EXPIRE_AT, REFRESH_TOKEN_EXPIRE_AT } from "../helpers/constant";
import { getDeviceInfo } from "../helpers/getDeviceInfo";
import { IJWTDecodedToken, IUser, TYPES } from "../helpers/types";
import { trackingLinksService } from "./tracking-links.service";

class AuthService {
  setToken(token: string, remember = false): void {
    cookie.set("token", token, {
      expires: new Date(new Date().getTime() + ACCESS_TOKEN_EXPIRE_AT),
    });
    // Clear disconnection flag when successfully setting a token
    localStorage.removeItem("isDisconnected");
  }

  setRefreshToken(token: string, remember = false): void {
    cookie.set("refreshToken", token, {
      expires: new Date(new Date().getTime() + REFRESH_TOKEN_EXPIRE_AT),
    });
  }

  getToken(name: string): string {
    return cookie.get(name) as string;
  }

  getRefreshToken(): string {
    return cookie.get("refreshToken") as string;
  }

  clearTokens(): void {
    cookie.remove("token");
    cookie.remove("refreshToken");
    localStorage.removeItem("deviceId");
  }

  isAuthenticated(): boolean {
    const token = this.getToken("token");
    if (token) {
      const decodedToken: any = jwt_decode(token);
      if (decodedToken.registrationStatus) {
        return true;
      }
      return false;
    } else {
      return false;
    }
  }

  isCreatorRegistrationComplete(): boolean {
    const token = this.getToken("token");
    if (token) {
      const decodedToken: any = jwt_decode(token);
      if (decodedToken.source === "creator") {
        return !decodedToken.signupStep;
      }
      return true;
    }
    return false;
  }

  getCreatorRegistrationStep(): string | null {
    const token = this.getToken("token");
    if (token) {
      const decodedToken: any = jwt_decode(token);
      if (decodedToken.source === "creator" && decodedToken.signupStep) {
        return decodedToken.signupStep;
      }
    }
    return null;
  }

  getId(): string {
    try {
      const token = this.getToken("token");
      const decodedToken: any = jwt_decode(token);
      return decodedToken.sourceId as string;
    } catch (e) {
      // console.log("error in getId", e);
      return "";
    }
  }

  getRole(): string {
    try {
      const token = this.getToken("token");
      const decodedToken: any = jwt_decode(token);
      return decodedToken.source as string;
    } catch (e) {
      // console.log("error in getRole", e);
      return "";
    }
  }

  getUser(): any {
    try {
      const token = this.getToken("token");
      const decodedToken: any = jwt_decode(token);
      return decodedToken;
    } catch (e) {
      // console.log("error in getUser", e);
      return "";
    }
  }

  getDecodedToken(): IJWTDecodedToken {
    try {
      const token = this.getToken("token");
      const decodedToken: any = jwt_decode(token);
      return decodedToken;
    } catch (e) {
      return {} as IJWTDecodedToken;
    }
  }

  getDeviceId(): string | null {
    return localStorage.getItem("deviceId");
  }

  login(usernameOrEmail: string, password: string, remember: boolean, device: any, navigate: any) {
    const url = `${process.env.REACT_APP_API_ENDPOINT}/auth/login`;

    return new Promise<boolean>((resolve, reject) => {
      axios
        .post(url, { usernameOrEmail, password, remember, device })
        .then(async (response: any) => {
          const token = response?.data?.data?.token || response?.data?.token;
          const refreshToken = response?.data?.data?.refreshToken || response?.data?.refreshToken;
          const deviceId = response?.data?.data?.deviceId || response?.data?.deviceId;

          if (token && refreshToken) {
            const decodedToken: any = jwt_decode(token);

            if ([TYPES.CREATOR, TYPES.FAN].includes(decodedToken.source)) {
              this.setToken(token, remember);
              this.setRefreshToken(refreshToken, remember);
              if (deviceId) {
                localStorage.setItem("deviceId", deviceId);
              }

              await trackingLinksService.saveLocalStorageVisitedProfileByTrackLink();

              if (decodedToken.source === TYPES.CREATOR && decodedToken.signupStep) {
                navigate(`/auth/signUp?id=${decodedToken.sourceId}&email=${decodedToken.email || ""}&currentTab=creator`);
                resolve(true);
              }
            } else if ([TYPES.MANAGER, TYPES.CHATTER].includes(decodedToken.source)) {
              const url = process.env.NODE_ENV === "development" ? "http://localhost:3300" : process.env.REACT_APP_BUSINESS_URL;

              window.location.replace(`${url}/auth/sign-in?isFromApp=true`);
              resolve(true);
            } else {
              reject(t("unauthorized-access"));
            }

            resolve(false);
          } else {
            reject(t("something-want-wrong"));
          }
        })
        .catch((error) => {
          console.log("error", error);
          if (error?.response?.data?.message) reject(error.response.data.message);
          else reject(t("something-want-wrong"));
        });
    });
  }

  async register(data: IUser) {
    const url = `${process.env.REACT_APP_API_ENDPOINT}/auth/register`;

    const device = await getDeviceInfo();
    return new Promise<IUser>((resolve, reject) => {
      axios
        .post(url, { ...data, device })
        .then(async (response: any) => {
          if (response?.data?.data?.token && response.data.data.refreshToken) {
            this.setToken(response.data.data.token, true);
            this.setRefreshToken(response.data.data.refreshToken, true);
            localStorage.setItem("deviceId", response.data.data.deviceId);

            await trackingLinksService.saveLocalStorageVisitedProfileByTrackLink();

            resolve(response.data.data);
          } else if (response?.data?.data) {
            resolve(response.data.data);
          } else reject(t("something-want-wrong"));
        })
        .catch((error) => {
          if (error?.response?.data?.message) reject(error.response.data.message);
          else reject(t("something-want-wrong"));
        });
    });
  }

  async googleSignUp(accessToken: string, type: string, sponsorId: string = "", isFightEvent: boolean = false) {
    const url = `${process.env.REACT_APP_API_ENDPOINT}/auth/google/register`;
    // const saveDeviceId = type === "fan";
    const device = await getDeviceInfo();

    return new Promise<{ data: IUser; status: number; token: string; refreshToken: string }>((resolve, reject) => {
      axios
        .post(url, { accessToken, type, sponsorId, device })
        .then(async (response: any) => {
          if (response?.data) {
            this.setToken(response.data.token, true);
            this.setRefreshToken(response.data.refreshToken, true);
            localStorage.setItem("deviceId", response.data.deviceId);

            await trackingLinksService.saveLocalStorageVisitedProfileByTrackLink();

            if (response?.data?.action === "login") {
              if (isFightEvent) {
                localStorage.setItem("redirectToEvent", "common/event-payment?fln=rtlf");
              }
              window.location.reload();
            } else {
              resolve({
                data: response?.data?.user,
                status: response.data.status,
                token: response.data.token,
                refreshToken: response.data.refreshToken,
              });
            }
          } else reject(t("something-want-wrong"));
        })
        .catch((error) => {
          if (error?.response?.data?.message) reject(error.response.data.message);
          else reject(t("something-want-wrong"));
        });
    });
  }

  async googleLogin(accessToken: string, navigate: any) {
    const url = `${process.env.REACT_APP_API_ENDPOINT}/auth/google/login`;
    const device = await getDeviceInfo();

    return new Promise<boolean>((resolve, reject) => {
      axios
        .post(url, { accessToken, device })
        .then(async (response: any) => {
          const token = response?.data?.token;
          const refreshToken = response?.data?.refreshToken;
          const deviceId = response?.data?.deviceId;

          if (token && refreshToken) {
            this.setToken(token, true);
            this.setRefreshToken(refreshToken, true);
            if (deviceId) {
              localStorage.setItem("deviceId", deviceId);
            }

            const decodedToken: any = jwt_decode(token);

            await trackingLinksService.saveLocalStorageVisitedProfileByTrackLink();

            if (decodedToken.source === "creator" && decodedToken.signupStep) {
              if (decodedToken.signupStep === "zero") {
                navigate(`/auth/signUp?currentTab=creator`);
              } else {
                navigate(`/auth/signUp?id=${decodedToken.sourceId}&email=${decodedToken.email || ""}&currentTab=creator`);
              }
              resolve(true);
              return;
            }

            resolve(false);
          } else {
            reject(t("something-want-wrong"));
          }
        })
        .catch((error) => {
          if (error?.response?.data?.message) reject(error.response.data.message);
          else reject(t("something-want-wrong"));
        });
    });
  }

  forgotPassword(email: string) {
    const url = `${process.env.REACT_APP_API_ENDPOINT}/auth/users/forgot`;

    return new Promise<string>((resolve, reject) => {
      axios
        .post(url, { email })
        .then((response) => {
          if (response?.data?.data?.success) resolve(response.data.data.success);
          else reject(t("something-want-wrong"));
        })
        .catch((error) => {
          if (error?.response?.data?.message) reject(error.response.data.message);
          else reject(t("something-want-wrong"));
        });
    });
  }

  changePassword(payload: any, token: string) {
    const url = `${process.env.REACT_APP_API_ENDPOINT}/auth/password-change?token=${token}`;

    return new Promise<string>((resolve, reject) => {
      axios
        .post(url, payload)
        .then((response) => {
          if (response?.data?.data?.success) resolve(response.data.data.success);
          else reject(t("something-want-wrong"));
        })
        .catch((error) => {
          if (error?.response?.data?.message) reject(error.response.data.message);
          else reject(t("something-want-wrong"));
        });
    });
  }

  signOut() {
    const url = `${process.env.REACT_APP_API_ENDPOINT}/auth/logout`;

    return new Promise<string>((resolve, reject) => {
      axiosInstance
        .post(url, { deviceId: this.getDeviceId() })
        .then((response) => {
          if (response?.data?.status) {
            posthog.reset();
            cookie.remove("token");
            cookie.remove("refreshToken");
            localStorage.removeItem("deviceId");

            resolve(response.data.success);
          } else reject(t("something-want-wrong"));
        })
        .catch((error) => {
          if (error?.response?.data?.message) reject(error.response.data.message);
          else reject(t("something-want-wrong"));
        });
    });
  }
}

export const authService = new AuthService();
